import { useRef } from 'react';

// How far a finger has to travel before a swipe opens or closes the sheet. Anything shorter
// snaps back, and a plain tap is left to the header's own click handler.
const SWIPE_PX = 40;

/**
 * Touch handlers for the mobile pick sheet's header (DraftBoard → PickPanelWrap). The sheet
 * follows the finger while it drags, then opens or closes on release.
 *
 * The drag writes the transform inline, since state updates per touchmove would re-render the
 * whole board. Release clears it, and PickPanelWrap's own transform and transition take over
 * from wherever the finger left the sheet.
 *
 * React registers touch listeners as passive, so these can't preventDefault — the header has
 * `touch-action: none` instead, which stops the browser scrolling or refreshing mid-swipe.
 *
 * sheetRef — the sheet element. peek — the height left showing when it's closed.
 */
const useSheetSwipe = ({ sheetRef, peek, open, setOpen }) => {
  const drag = useRef(null);

  const onTouchStart = (e) => {
    const el = sheetRef.current;
    if (!el) return;
    drag.current = {
      startY: e.touches[0].clientY,
      // Where the sheet is now, mid-transition included, so grabbing it never makes it jump.
      baseY: new DOMMatrix(getComputedStyle(el).transform).m42,
      dy: 0,
    };
    el.style.transition = 'none';
  };

  const onTouchMove = (e) => {
    const el = sheetRef.current;
    const d = drag.current;
    if (!el || !d) return;
    d.dy = e.touches[0].clientY - d.startY;
    const closedY = el.offsetHeight - peek;
    const y = Math.min(Math.max(d.baseY + d.dy, 0), closedY);
    el.style.transform = `translateY(${y}px)`;
  };

  // A cancelled touch (the browser took the gesture) only snaps back.
  const release = (commit) => {
    const el = sheetRef.current;
    const d = drag.current;
    drag.current = null;
    if (!el || !d) return;
    el.style.transition = '';
    el.style.transform = '';
    if (!commit) return;
    if (d.dy <= -SWIPE_PX && !open) setOpen(true);
    else if (d.dy >= SWIPE_PX && open) setOpen(false);
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd: () => release(true),
    onTouchCancel: () => release(false),
  };
};

export default useSheetSwipe;
